import { type SyntheticEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { authService } from '../../auth/service';
import { Button, Card, Icon, TextField } from '@humanforce/mango-ui-react';

interface LocationState {
  from?: string;
}

// The underlying Mango input element exposes its current value (and the inner native input) as properties.
type MangoInputElement = HTMLElement & { value?: string; input?: HTMLInputElement };

const readFieldValue = (id: string) => {
  const el = document.getElementById(id) as MangoInputElement | null;
  return el?.input?.value ?? el?.value ?? '';
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const from = (location.state as LocationState | null)?.from ?? '/';

  // Passive browser autofill populates the inputs without firing an input event, so React state stays
  // empty and the submit button is wrongly disabled. Focusing the email field on mount upgrades the
  // Mango input and prompts the browser to commit the autofill, which makes the polled values below
  // readable (they stay hidden until a field is focused). Poll briefly afterwards to capture them.
  useEffect(() => {
    const el = document.getElementById('login-email') as MangoInputElement | null;
    (el?.input ?? el)?.focus?.();

    const sync = () => {
      setEmail((current) => current || readFieldValue('login-email'));
      setPassword((current) => current || readFieldValue('login-password'));
    };
    const timers = [100, 300, 600, 1000].map((delay) => window.setTimeout(sync, delay));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, []);

  const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await authService.login(email, password);
      navigate(from, { replace: true });
    } catch {
      setError('Wrong email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '24rem' }}>
      <Card
        header={
          <div className="mgo-cluster mgo-gap-m">
            <span style={{ fontSize: 'var(--mgo-size-40)' }}><Icon name="brand-payroll" /></span>
            <h1 className="mgo-text-l mgo-weight-strong">Humanforce Payroll</h1>
          </div>
        }
      >
        <form className="mgo-stack mgo-gap-l" onSubmit={(event) => void handleSubmit(event)}>
          <TextField id="login-email" name="email" autoComplete="username" label="Email" type="email" value={email} disabled={isSubmitting} onChange={(e) => setEmail(e.target.value)} />
          <TextField id="login-password" name="password" autoComplete="current-password" label="Password" type="password" value={password} disabled={isSubmitting} onChange={(e) => setPassword(e.target.value)} />
          {error && <p className="mgo-text-s mgo-color-critical" role="alert" style={{ margin: 0 }}>{error}</p>}
          <Button type="submit" loading={isSubmitting} disabled={!email || !password}>
            Sign in
          </Button>
        </form>
      </Card>
    </div>
  );
};
